/**
 * A function that maps a numeric value to a CSS color string.
 */
export type ColorScale = (value: number) => string;

/**
 * Add alpha transparency to a hex color.
 *
 * @param hex - 6-digit hex color (e.g., "#56B4E9")
 * @param alpha - alpha value 0-1 (default 0.6 for background readability)
 * @returns 8-digit hex color with alpha
 */
export function withAlpha(hex: string, alpha = 0.6): string {
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${alphaHex}`;
}

/**
 * Nucleotide colors (colorblind-safe, Okabe-Ito inspired).
 *
 * Use withAlpha() when rendering backgrounds with text on top.
 *
 * Degenerate codes use muted tones to indicate ambiguity:
 * - R (A|G), Y (C|T), S (G|C), W (A|T), K (G|T), M (A|C)
 * - B (not A), D (not C), H (not G), V (not T)
 * - N (any), U (uracil, RNA)
 */
export const NUCLEOTIDE_COLORS: Record<string, string> = {
  // Primary bases
  A: "#56B4E9", // sky blue
  T: "#E69F00", // orange
  C: "#009E73", // bluish green
  G: "#F0E442", // yellow
  U: "#CC79A7", // reddish purple (RNA)

  // Two-base ambiguity
  R: "#89CFF0", // light blue (A|G)
  Y: "#FFB347", // light orange (C|T)
  S: "#66CDAA", // medium aquamarine (G|C)
  W: "#87CEEB", // sky blue light (A|T)
  K: "#F4D03F", // lighter yellow (G|T)
  M: "#48D1CC", // medium turquoise (A|C)

  // Three-base ambiguity
  B: "#DDA0DD", // plum (not A)
  D: "#F5DEB3", // wheat (not C)
  H: "#98FB98", // pale green (not G)
  V: "#ADD8E6", // light blue (not T)

  // Any
  N: "#C0C0C0", // silver
};

/**
 * Amino acid colors (colorblind-safe, Okabe-Ito palette).
 *
 * Use withAlpha() when rendering backgrounds with text on top.
 */
export const AMINO_ACID_COLORS: Record<string, string> = {
  // Hydrophobic - aliphatic (orange)
  A: "#E69F00",
  V: "#E69F00",
  I: "#E69F00",
  L: "#E69F00",
  M: "#E69F00",
  // Hydrophobic - aromatic (vermillion)
  F: "#D55E00",
  W: "#D55E00",
  P: "#D55E00",
  // Polar (bluish green)
  S: "#009E73",
  T: "#009E73",
  N: "#009E73",
  Q: "#009E73",
  C: "#009E73",
  G: "#009E73",
  // Positive charge (sky blue)
  K: "#56B4E9",
  R: "#56B4E9",
  H: "#56B4E9",
  // Negative charge (reddish purple)
  D: "#CC79A7",
  E: "#CC79A7",
  // Aromatic - special (yellow)
  Y: "#F0E442",
  // Stop codon (dark gray)
  "*": "#999999",
  // Gap (transparent)
  "-": "#ffffff00",
  // Unknown (light gray)
  X: "#CCCCCC",
  // Ambiguous
  B: "#CCCCCC",
  Z: "#CCCCCC",
};

/**
 * Color scale for 0-1 scores (conservation, identity, etc.).
 * Higher scores get darker blues with decreasing transparency.
 */
export function getScoreColor(score: number): string {
  if (score >= 1.0) return "#1e40af80";
  if (score >= 0.8) return "#3b82f660";
  if (score >= 0.6) return "#60a5fa50";
  if (score >= 0.4) return "#93c5fd40";
  if (score >= 0.2) return "#bfdbfe30";
  return "#f3f4f620";
}

/**
 * Color scale for gap percentages (0-1).
 * Higher gap percentages get warmer colors (red = bad).
 */
export function getGapColor(gapPercentage: number): string {
  if (gapPercentage >= 0.8) return "#dc2626";
  if (gapPercentage >= 0.6) return "#f97316";
  if (gapPercentage >= 0.4) return "#facc15";
  if (gapPercentage >= 0.2) return "#84cc16";
  return "#22c55e";
}

/**
 * Color scale for normalized entropy values (0-1).
 * Higher entropy gets purple; lower gets blue.
 */
export function getEntropyColor(normalizedEntropy: number): string {
  if (normalizedEntropy >= 0.8) return "#8b5cf6";
  if (normalizedEntropy >= 0.6) return "#a78bfa";
  if (normalizedEntropy >= 0.4) return "#7dd3fc";
  if (normalizedEntropy >= 0.2) return "#38bdf8";
  return "#0ea5e9";
}

/**
 * Colors for GenBank feature types.
 */
export const FEATURE_COLORS: Record<string, string> = {
  CDS: "#3b82f6", // blue
  gene: "#22c55e", // green
  promoter: "#f59e0b", // amber
  terminator: "#ef4444", // red
  rep_origin: "#8b5cf6", // purple
  primer_bind: "#06b6d4", // cyan
  misc_feature: "#6b7280", // gray
  regulatory: "#ec4899", // pink
  exon: "#14b8a6", // teal
  intron: "#a3a3a3", // neutral gray
  "5'UTR": "#f97316", // orange
  "3'UTR": "#84cc16", // lime
  mRNA: "#10b981", // emerald
  tRNA: "#0ea5e9", // sky
  rRNA: "#6366f1", // indigo
};

/**
 * Colors for feature strands.
 */
export const STRAND_COLORS = {
  forward: "#3b82f6", // blue
  reverse: "#ef4444", // red
};

/**
 * Get the color for a feature based on its type.
 * Falls back to gray for unknown types.
 */
export function getFeatureColor(featureType: string): string {
  return FEATURE_COLORS[featureType] ?? "#6b7280";
}
